import { BaseDialog } from '@/components/BaseDialog'
import { DatePicker } from '@/components/DatePicker'
import { ImageUpload } from '@/components/ImageUpload'
import { QRScannerDialog, QRScannerDialogRef } from '@/components/QRScannerDialog'
import { SelectedEquipmentCard } from '@/components/SelectedEquipmentCard'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { MultiSelect, MultiSelectGroup } from '@/components/ui/multi-select'
import { SearchableSelect, type SearchableSelectGroup } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useGetDepartmentItems, useGetDepartmentOptions } from '@/features/departments/hooks/useDepartment'
import { useGetConfigs } from '@/features/settings/hooks/useConfig'
import { AppConfigKey } from '@/features/settings/types/config'
import { useFileUploadField } from '@/hooks/useFileUpload'
import { useUI } from '@/hooks/useUI'
import { toUtcDateString } from '@/utils/date'
import { isApiError } from '@/utils/error'
import { formatBuildingFloorLocation } from '@/utils/location'
import { showError, showSuccess } from '@/utils/toast'
import { Camera, ScanLine, Truck } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateReturnTransferRequest, useCreateTransferRequest } from '../hooks/useTransferRequest'

const emptyForm = {
  sourceDepartmentId: '',
  itemIds: [] as string[],
  targetDepartmentId: '',
  reason: '',
  scheduledDate: '',
  deliveryMethod: 'Deliver',
}
const selectFieldClassName = 'flex w-full flex-col gap-1.5'
const selectTriggerClassName = 'w-full bg-secondary/50 text-xs'

export function TransferOutDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation()
  const { deliveryMethods } = useUI()
  const { data: configs } = useGetConfigs()
  const { departments: userDepartments, refreshMe, isRefreshingMe } = useAuth()
  const [form, setForm] = useState(emptyForm)
  const [imageIds, setImageIds] = useState<string[]>([])
  const { data: sourceDepartmentItems, isLoading: isLoadingItems } = useGetDepartmentItems(form.sourceDepartmentId, {
    status: 'Available',
  })

  const createTransferRequest = useCreateTransferRequest()
  const createReturnTransferRequest = useCreateReturnTransferRequest()
  const imageField = useFileUploadField({
    value: imageIds,
    onChange: setImageIds,
    maxFiles: 4,
    onUploadError: error => showError(isApiError(error) ? error.message : t('common.uploadImageError')),
  })

  const scannerRef = useRef<QRScannerDialogRef>(null)

  const sourceItems = sourceDepartmentItems?.items ?? []
  const selectedItems = sourceItems.filter(item => form.itemIds.includes(item.id))
  const selectedFirstItem = selectedItems[0]
  const selectedProductId = sourceItems.find(item => item.id === form.itemIds[0])?.productId

  const itemDepartmentId = selectedFirstItem?.departmentId ?? form.sourceDepartmentId
  const { data: departments = [], isLoading: isLoadingDepartments } = useGetDepartmentOptions(
    {
      excludeDepartmentId: itemDepartmentId || undefined,
    },
    open && !!itemDepartmentId,
  )

  const warehouseDepartmentId = configs?.[AppConfigKey.WarehouseDepartmentId]
  const sourceWarehouseDepartment = userDepartments.find(d => d.id === warehouseDepartmentId)
  const sourceDepartmentGroups: SearchableSelectGroup[] = useMemo(
    () => [
      {
        label: t('requests.mainWarehouse'),
        options: sourceWarehouseDepartment
          ? [
              {
                value: sourceWarehouseDepartment.id,
                label: sourceWarehouseDepartment.name,
                description: formatBuildingFloorLocation(
                  t,
                  sourceWarehouseDepartment.buildingName,
                  sourceWarehouseDepartment.floorName,
                ),
              },
            ]
          : [],
      },
      {
        label: t('requests.otherDepartments'),
        options: userDepartments
          .filter(d => d.id !== warehouseDepartmentId)
          .map(department => ({
            value: department.id,
            label: department.name,
            description: formatBuildingFloorLocation(t, department.buildingName, department.floorName),
            searchValue: `${department.name} ${department.buildingName || ''} ${department.floorName || ''}`,
          })),
      },
    ],
    [userDepartments, itemDepartmentId, t],
  )

  const itemMap = useMemo(() => {
    return new Map((sourceDepartmentItems?.items ?? []).map(item => [item.id, item]))
  }, [sourceDepartmentItems?.items])

  const itemGroups = useMemo<MultiSelectGroup[]>(() => {
    const groups = new Map<string, MultiSelectGroup>()

    sourceDepartmentItems?.items.forEach(item => {
      if (!groups.has(item.productId)) {
        groups.set(item.productId, {
          label: item.productName || `${item.productBrand} ${item.productModel}`,
          options: [],
        })
      }

      groups.get(item.productId)!.options.push({
        value: item.id,
        label: item.serialNumber,
        searchValue: `${item.serialNumber} ${item.productName} ${item.productBrand} ${item.productModel}`,
        disabled: !!selectedProductId && item.productId !== selectedProductId,
      })
    })

    return [...groups.values()]
  }, [sourceDepartmentItems?.items, selectedProductId])

  const ownerDepartmentId = selectedFirstItem?.ownerDepartmentId
  const ownerDepartmentName = selectedFirstItem?.ownerDepartmentName
  const targetWarehouseDepartment = departments.find(d => d.id === warehouseDepartmentId)
  const targetDepartmentGroups: SearchableSelectGroup[] = useMemo(
    () => [
      {
        label: t('requests.mainDepartment'),
        options:
          ownerDepartmentId && ownerDepartmentId !== form.sourceDepartmentId
            ? [
                {
                  value: ownerDepartmentId,
                  label: ownerDepartmentName ?? '',
                  description: formatBuildingFloorLocation(
                    t,
                    selectedFirstItem.buildingName,
                    selectedFirstItem.floorName,
                  ),
                },
              ]
            : [],
      },
      {
        label: t('requests.mainWarehouse'),
        options: targetWarehouseDepartment
          ? [
              {
                value: targetWarehouseDepartment.id,
                label: targetWarehouseDepartment.name,
                description: formatBuildingFloorLocation(
                  t,
                  targetWarehouseDepartment.buildingName,
                  targetWarehouseDepartment.floorName,
                ),
              },
            ]
          : [],
      },
      {
        label: t('requests.otherDepartments'),
        options: departments
          .filter(department => department.id !== ownerDepartmentId && department.id !== warehouseDepartmentId)
          .map(department => ({
            value: department.id,
            label: department.name,
            description: formatBuildingFloorLocation(t, department.buildingName, department.floorName),
          })),
      },
    ],
    [t, departments, form.sourceDepartmentId, ownerDepartmentId],
  )
  const isReturningToOwnerDepartment = !!ownerDepartmentId && form.targetDepartmentId === ownerDepartmentId
  const isSubmitting =
    createTransferRequest.isPending || createReturnTransferRequest.isPending || imageField.isUploading

  useEffect(() => {
    if (!open) {
      setForm(emptyForm)
      setImageIds([])
      return
    }

    refreshMe().catch(error => {
      if (isApiError(error)) showError(error.message)
    })
  }, [open, refreshMe])

  const handleScan = useCallback(
    (text: string) => {
      try {
        const url = new URL(text)
        const match = url.pathname.match(/\/scan\/equipment\/([^/]+)/)
        const itemId = match?.[1]

        if (!itemId) {
          showError(t('common.qrScanner.invalidQr'))
          return
        }

        const item = itemMap.get(itemId)
        if (!item) {
          showError(t('common.qrScanner.equipmentNotFound'))
          return
        }

        setForm(prev => {
          if (prev.itemIds.includes(itemId)) {
            showError(t('common.qrScanner.equipmentAlreadySelected'))
            return prev
          }

          if (selectedProductId && item.productId !== selectedProductId) {
            showError(t('common.qrScanner.differentEquipmentModel'))
            return prev
          }

          return {
            ...prev,
            itemIds: [...prev.itemIds, itemId],
          }
        })
      } catch {
        showError(t('common.qrScanner.invalidQr'))
      }
    },
    [sourceDepartmentItems?.items, selectedProductId, t],
  )

  function handleSourceDepartmentChange(sourceDepartmentId: string) {
    setForm(current => ({
      ...current,
      sourceDepartmentId,
      itemIds: [],
      targetDepartmentId: current.targetDepartmentId === sourceDepartmentId ? '' : current.targetDepartmentId,
    }))
  }

  async function handleSubmit() {
    if (
      !form.sourceDepartmentId ||
      form.itemIds.length === 0 ||
      !form.targetDepartmentId ||
      !form.reason ||
      !form.scheduledDate ||
      !form.deliveryMethod ||
      imageIds.length === 0
    )
      return

    try {
      const payload = {
        productId: selectedFirstItem.productId,
        itemIds: form.itemIds,
        targetDepartmentId: form.targetDepartmentId,
        scheduledDate: toUtcDateString(form.scheduledDate),
        deliveryMethod: form.deliveryMethod,
        reason: form.reason,
        imageIds,
      }

      if (isReturningToOwnerDepartment) {
        await createReturnTransferRequest.mutateAsync(payload)
      } else {
        await createTransferRequest.mutateAsync(payload)
      }

      setForm(emptyForm)
      setImageIds([])
      showSuccess(t('requests.requestSuccess'))
      onOpenChange(false)
    } catch (error) {
      showError(isApiError(error) ? error.message : t('requests.requestError'))
    }
  }

  return (
    <>
      <BaseDialog
        open={open}
        onOpenChange={onOpenChange}
        contentTestId="transfer-out-dialog"
        title={t('requests.transferOutTitle')}
        footer={
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="text-xs">
              {t('common.cancel')}
            </Button>
            <Button
              data-testid="transfer-out-submit"
              onClick={() => (imageIds.length === 0 ? showError(t('requests.attachReceiveImage')) : handleSubmit())}
              className="text-xs"
              disabled={
                !form.sourceDepartmentId ||
                form.itemIds.length === 0 ||
                !form.targetDepartmentId ||
                !form.reason ||
                !form.scheduledDate ||
                !form.deliveryMethod ||
                isRefreshingMe ||
                isLoadingItems ||
                isLoadingDepartments ||
                isSubmitting
              }
            >
              <Truck className="mr-1 size-3" />
              {isSubmitting
                ? t('common.submitting')
                : isReturningToOwnerDepartment
                  ? t('requests.returnEquipment')
                  : t('requests.transferEquipment')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4 pt-2">
          <div className={selectFieldClassName}>
            <Label className="text-xs">{t('requests.chooseTransferDepartment')} *</Label>
            <SearchableSelect
              value={form.sourceDepartmentId}
              onValueChange={handleSourceDepartmentChange}
              groups={sourceDepartmentGroups}
              placeholder={t('requests.chooseTransferDepartment')}
              searchPlaceholder={t('common.placeholder.searchDepartment')}
              emptyText={isRefreshingMe ? t('common.loading') : t('common.noDepartment')}
              triggerClassName={selectTriggerClassName}
              testId="transfer-out-source-department"
            />
          </div>
          <div className={selectFieldClassName}>
            <div className="flex justify-between gap-2">
              <Label className="text-xs">{t('requests.chooseEquipmentToTransfer')} *</Label>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => scannerRef.current?.start()}
                disabled={isLoadingItems}
                title={t('common.qrScanner.scanButton')}
              >
                <ScanLine className="size-4" />
              </Button>
            </div>

            <MultiSelect
              value={form.itemIds}
              groups={itemGroups}
              onValueChange={itemIds =>
                setForm(prev => ({
                  ...prev,
                  itemIds,
                }))
              }
              placeholder={
                form.sourceDepartmentId
                  ? t('requests.chooseEquipmentToTransfer')
                  : t('common.placeholder.selectDepartmentFirst')
              }
              searchPlaceholder={t('common.placeholder.searchEquipment')}
              emptyText={isLoadingItems ? t('common.loading') : t('requests.noEquipment')}
              testId="transfer-out-items"
            />
          </div>
          {selectedFirstItem && selectedItems && (
            <SelectedEquipmentCard
              name={selectedFirstItem.productName}
              serialNumbers={selectedItems.map(item => item.serialNumber)}
              brand={selectedFirstItem.productBrand}
              model={selectedFirstItem.productModel}
              departmentName={selectedFirstItem.departmentName}
              buildingName={selectedFirstItem.buildingName}
              floorName={selectedFirstItem.floorName}
            />
          )}
          <div className={selectFieldClassName}>
            <Label className="text-xs">{t('requests.targetDepartment')} *</Label>
            <SearchableSelect
              value={form.targetDepartmentId}
              onValueChange={targetDepartmentId => setForm({ ...form, targetDepartmentId })}
              groups={targetDepartmentGroups}
              placeholder={
                form.itemIds.length > 0 ? t('requests.chooseTargetDepartment') : t('requests.selectEquipmentFirst')
              }
              searchPlaceholder={t('common.placeholder.searchDepartment')}
              emptyText={isLoadingDepartments ? t('common.loading') : t('common.noDepartment')}
              disabled={form.itemIds.length === 0}
              triggerClassName={selectTriggerClassName}
              testId="transfer-out-target-department"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">{t('requests.scheduleDate')} *</Label>
              <DatePicker
                testId="transfer-out-scheduled-date"
                value={form.scheduledDate}
                onChange={scheduledDate => setForm({ ...form, scheduledDate })}
                placeholder={t('requests.chooseScheduleDate')}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">{t('requests.method')} *</Label>
              <Select value={form.deliveryMethod} onValueChange={v => setForm({ ...form, deliveryMethod: v })}>
                <SelectTrigger
                  data-testid="transfer-out-delivery-method"
                  className="h-9 w-full min-w-0 bg-secondary/50 text-xs [&>span]:truncate"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card text-foreground">
                  {deliveryMethods?.map(method => (
                    <SelectItem
                      data-testid={`transfer-out-delivery-method-option-${method.value}`}
                      key={method.value}
                      value={method.value}
                    >
                      {method.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{t('requests.reason')} *</Label>
            <Textarea
              data-testid="transfer-out-reason"
              value={form.reason}
              onChange={e => setForm({ ...form, reason: e.target.value })}
              className="bg-secondary/50 text-xs"
              rows={2}
              placeholder={t('requests.reasonTransferPlaceholder')}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="flex items-center gap-1 text-xs">
              <Camera className="size-3" />
              {t('requests.uploadImages')} *
            </Label>
            <ImageUpload
              files={imageField.files}
              imageIds={imageField.imageIds}
              onChange={imageField.handleChange}
              onRemoveExisting={imageField.handleRemoveExisting}
              maxImages={4}
              testId="transfer-out-images"
            />
            {imageField.isUploading ? (
              <p className="text-[11px] text-muted-foreground">
                {t('common.uploadingImages')}
                {imageField.uploadProgress ? ` ${imageField.uploadProgress}%` : '...'}
              </p>
            ) : null}
          </div>
        </div>
      </BaseDialog>

      <QRScannerDialog ref={scannerRef} onScan={handleScan} />
    </>
  )
}
